import React, { useState } from "react"
import { validateEmail } from "../../helpers/email-input.helper"
import {
  WiderContainer,
  ArticleCTAContainer,
  ContentContainer,
  EmailSubscribeHorizontalSubTitle,
  EmailSubscribeHorizontalInput,
  EmailSubscribeStatus,
  StatusIcon,
  EmailSubscribeHorizontalFormContainer,
} from "./article-CTA.styles"
import { ThemeProvider } from "styled-components"
import theme from "../../themes/theme"
import dataLayerHelper from "../../helpers/datalayer.helper"
import ButtonLink from "../btn-collections/btn-collections.component"
import { monthliesEventHandler } from "../../../email/monthlies.function"
import { guidesEventHandler } from "../../../email/guides.functions"
import { blogpostEventHandler } from "../../../email/blogposts.function"

const ArticleCTA = ({
  data,
  label,
  slug,
  tags,
  isTop,
  isMiddle,
  isMonthlyPost,
  datasets,
}) => {
  
  const { versionA, title, desc, author } = data
  
  // IF MONTHLY POST, DEFINE WHICH MONTHLY POST IT IS
  let whichMonthlyPost = ''
  if(isMonthlyPost) {
    if (tags.includes("tag-WDM")) whichMonthlyPost = "Web Dev Monthly"
    if (tags.includes("tag-MLM")) whichMonthlyPost = "Machine Learning Monthly"
    if (tags.includes("tag-PM")) whichMonthlyPost = "Python Monthly" 
  }

  // CREATE FALLBACK DESCRIPTION FOR THE POST INCASE ONE IS NOT PROVIDED
  // THE DEFAULT WILL BE DIFFERENT FOR MONTHLY POSTS AND REGULAR POSTS
  const fallBackMarkdown = isMonthlyPost
    ? `<p>If you haven't already, subscribe below to receive the best ${whichMonthlyPost} Newsletter, along with exclusive ZTM posts and offers. If you're already subscribed, please share it with someone who might find it useful. They'll love you and we will too!</p>`
    : `<p>If you enjoyed ${author}'s post and want to get more like it in the future, subscribe below. By joining over 300,000 ZTM email subscribers, you'll receive exclusive ZTM posts, opportunities, and offers.</p>`

  // IF DESCRIPTION IS PROVIDED, USE IT, ELSE USE THE FALLBACK
  const ctaDesc = desc ? desc.childMarkdownRemark.html : fallBackMarkdown

  // DETERMINE THE POSITION OF THE CTA
  const position = isTop ? "top" : isMiddle ? "middle" : "bottom"

  // HERE WE ARE DETERMINING THE CONTENT OF THE CTA
  // IF IT'S A TOP OR MIDDLE CTA, WE USE THE DATA FROM CONTENTFUL's `NEW EMAIL CTA` MODEL
  // (This is where that customGTM tag is still used I mentioned in Markdown.helper.jsx:45)
  function renderCTAContent(isTop, isMiddle) {
    const tagArr = tags.split(", ")
    if (isTop || isMiddle) {
      const TopCta = data.topCta.filter(
        (cta) => cta.blogId === `${tagArr[tagArr.length - 1]}-${position}`
      )[0]
      const TopCtaTitle = TopCta.blogCtaHeadline
      const TopCtaDesc = TopCta.blogCtaDescription.childMarkdownRemark.html
      return [TopCtaTitle, TopCtaDesc]
    } else {
      return [title, ctaDesc]
    }
  }

  const [blogTitle, blogDesc] = renderCTAContent(isTop, isMiddle)
  const [email, setEmail] = useState("")
  const [statusText, setStatusText] = useState("")
  const [statusStyle, setStatusStyle] = useState("default")

  const updateGTM = (status) => {
    dataLayerHelper({
      event: "emailSubscriber",
      status,
      cta_identifier: tags,
    })
  }

  const postSubscriber = async (email) => {
    // VALIDATE EMAIL
    if (!validateEmail(email)) {
      setStatusText(`Please provide a valid email address to stay up to date!`)
      setStatusStyle(`error`)
      return
    }

    const handleSubscriptionResponse = async (handler) => {
      if (handler.status === 200) {
        setStatusText("Great decision, you've successfully subscribed!")
        setStatusStyle("success")
        updateGTM("Subscribe Success")
      } else {
        setStatusText("An error occurred, please try again!")
        setStatusStyle("error")
      }
    }

    try {
      // HANDLE MONTHLY NEWSLETTER SUBSCRIPTION
      if (isMonthlyPost) {
        const handler = await monthliesEventHandler({
          email,
          isMonthlyPost,
          monthly: tags.replace("tag-", ""),
        })

        // Handle submission response
        await handleSubscriptionResponse(handler)
      }

      // ## HANDLE GUIDE BLOG POSTS ##
      // The div for the CTA in these guides will have a dataset attribute
      // Eg. <div data-guide="true">...</div>
      // Currently:
      //   /blog/best-way-to-learn-python-for-free/
      //   /blog/learn-to-code-for-free/
      if (datasets.guide) {
        const handler = await guidesEventHandler({
          email,
          guide: slug,
        })

        // Handle submission response
        await handleSubscriptionResponse(handler)
      }

      // ## HANDLE REGULAR BLOGPOST SUBSCRIPTIONS ##
      else if (!isMonthlyPost && !datasets.guide) {
        const handler = await blogpostEventHandler({
          email,
          blogpost: slug,
        })

        // Handle submission response
        await handleSubscriptionResponse(handler)
      }
    } catch (error) {
      setStatusText(`An error occured, please try again!`)
      setStatusStyle(`error`)
    }
  }

  const handleEmailChange = (e) => {
    setEmail(e.target.value.trim())
  }
  const displayStatusIcon = (state) => {
    switch (state) {
      case "success":
        return (
          <StatusIcon statusStyle={state}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 25 25"
              width="25"
              height="25"
            >
              <circle fill="#1c8000" cx="11.9" cy="11.9" r="11.5" />
              <path
                stroke="white"
                d="M 17.3789 6.78271 L 8.33081 15.8308 L 4.65625 12.1562"
              />
            </svg>
          </StatusIcon>
        )

      case "error":
        return (
          <StatusIcon statusStyle={state}>
            <svg
              viewBox="0 0 25 25"
              width="25"
              height="25"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle fill="#EB000E" cx="11.9" cy="11.9" r="11.5" />
              <line
                x1="16.3809"
                y1="6.78275"
                x2="6.78421"
                y2="16.3794"
                stroke="white"
              />
              <line
                x1="16.4121"
                y1="16.3794"
                x2="6.81539"
                y2="6.78275"
                stroke="white"
              />
            </svg>
          </StatusIcon>
        )
      default:
        return null
    }
  }

  const displayStatusText = (state) => {
    if (state !== "default") {
      return (
        <EmailSubscribeStatus statusStyle={state}>
          {statusText}
        </EmailSubscribeStatus>
      )
    } else return null
  }
  theme.versionA = versionA
  // for some reasons articleCTA doesn't have access to the theme
  return (
    <ThemeProvider theme={theme}>
      <WiderContainer mt={[60, 60, 80, 80]} mb={[60, 60, 80, 80]}>
        <ArticleCTAContainer versionA={versionA}>
          <ContentContainer className="content-container">
            <h4>{blogTitle || label}</h4>
            <div dangerouslySetInnerHTML={{ __html: blogDesc }} />

            {displayStatusText(statusStyle)}

            <EmailSubscribeHorizontalFormContainer>
              <EmailSubscribeHorizontalInput
                statusStyle={statusStyle}
                placeholder="Email"
                onKeyDown={(e) =>
                  e.key === "Enter" ? postSubscriber(email) : null
                }
                onChange={handleEmailChange}
                type="email"
                autoComplete="email"
              />

              <ButtonLink isButton onClick={() => postSubscriber(email)}>
                SUBSCRIBE
              </ButtonLink>

              {displayStatusIcon(statusStyle)}
            </EmailSubscribeHorizontalFormContainer>

            <EmailSubscribeHorizontalSubTitle>
              No spam ever, unsubscribe anytime
            </EmailSubscribeHorizontalSubTitle>
          </ContentContainer>
        </ArticleCTAContainer>
      </WiderContainer>
    </ThemeProvider>
  )
}
export default ArticleCTA
